import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Building2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { api } from "../../lib/api";
import { useSettings } from "../../lib/settings-context";

const E164 = /^\+[1-9]\d{7,14}$/;

/** Phase 25 — Ajustes now also exposes company legal identity fields consumed
 *  by the AI legal generator. All fields are optional but strongly encouraged
 *  because empty ones surface as «[Pendiente de completar]» in the drafts. */
export default function AdminAjustes() {
  const { refresh } = useSettings();
  const [form, setForm] = useState({
    whatsapp_number: "",
    whatsapp_default_greeting: "",
    whatsapp_floating_enabled: true,
    company_legal_name: "",
    company_trade_name: "",
    company_tax_id: "",
    company_address: "",
    company_email: "",
    company_phone: "",
    company_dpo_email: "",
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/admin/settings")
      .then((r) => {
        const d = r.data || {};
        setForm((f) => ({
          ...f,
          whatsapp_number: d.whatsapp_number || "",
          whatsapp_default_greeting: d.whatsapp_default_greeting || "",
          whatsapp_floating_enabled:
            typeof d.whatsapp_floating_enabled === "boolean"
              ? d.whatsapp_floating_enabled
              : true,
          company_legal_name: d.company_legal_name || "",
          company_trade_name: d.company_trade_name || "",
          company_tax_id: d.company_tax_id || "",
          company_address: d.company_address || "",
          company_email: d.company_email || "",
          company_phone: d.company_phone || "",
          company_dpo_email: d.company_dpo_email || "",
        }));
      })
      .finally(() => setLoading(false));
  }, []);

  const numberOk = E164.test(form.whatsapp_number);

  const save = async () => {
    if (!numberOk) {
      toast.error("Número no válido (formato E.164 requerido).");
      return;
    }
    if ((form.whatsapp_default_greeting || "").length > 200) {
      toast.error("El mensaje no puede superar 200 caracteres.");
      return;
    }
    setSaving(true);
    try {
      await api.patch("/admin/settings", form);
      toast.success("Ajustes guardados");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudieron guardar los ajustes");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="h-48 rounded-2xl bg-neutral-100 animate-pulse" />;
  }

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div data-testid="admin-ajustes" className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-3xl font-black tracking-tight">Ajustes</h1>
        <p className="text-sm text-neutral-500 mt-1">
          Configura tu número de WhatsApp y la identidad legal que se usa para generar los documentos legales con IA.
        </p>
      </div>

      {/* WhatsApp block */}
      <div className="rounded-2xl border border-neutral-200 bg-white p-6 space-y-5">
        <div>
          <Label htmlFor="whatsapp-number">Número de WhatsApp</Label>
          <Input
            id="whatsapp-number"
            data-testid="ajustes-whatsapp-number"
            value={form.whatsapp_number}
            placeholder="+34612345678"
            onChange={(e) =>
              setForm((f) => ({ ...f, whatsapp_number: e.target.value.trim() }))
            }
            className={`mt-1 ${
              form.whatsapp_number && !numberOk
                ? "border-red-400 focus-visible:ring-red-200"
                : ""
            }`}
          />
          <p
            className={`text-xs mt-1 ${
              form.whatsapp_number && !numberOk
                ? "text-red-600"
                : "text-neutral-500"
            }`}
          >
            {form.whatsapp_number && !numberOk
              ? "Número no válido. Formato: +34612345678 (E.164, con prefijo internacional)."
              : "Formato: +34612345678 (E.164, con prefijo internacional)."}
          </p>
        </div>

        <div>
          <Label htmlFor="whatsapp-greeting">Mensaje por defecto</Label>
          <Textarea
            id="whatsapp-greeting"
            data-testid="ajustes-whatsapp-greeting"
            rows={3}
            maxLength={200}
            value={form.whatsapp_default_greeting}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                whatsapp_default_greeting: e.target.value.slice(0, 200),
              }))
            }
            className="mt-1"
          />
          <p className="text-xs text-neutral-500 mt-1">
            {(form.whatsapp_default_greeting || "").length}/200 · Se abrirá con este texto pre-escrito.
          </p>
        </div>

        <label className="flex items-center gap-3">
          <Switch
            checked={!!form.whatsapp_floating_enabled}
            onCheckedChange={(v) =>
              setForm((f) => ({ ...f, whatsapp_floating_enabled: !!v }))
            }
            data-testid="ajustes-whatsapp-floating"
          />
          <span className="text-sm">Mostrar botón flotante de WhatsApp</span>
        </label>
      </div>

      {/* Phase 25 — Company legal identity block */}
      <div className="rounded-2xl border border-neutral-200 bg-white p-6 space-y-4">
        <div className="flex items-start gap-2">
          <Building2 className="h-5 w-5 text-[#00953B] mt-0.5" />
          <div>
            <h2 className="font-bold text-lg leading-tight">Identidad legal (empresa)</h2>
            <p className="text-xs text-neutral-500">
              Estos datos alimentan al generador de páginas legales con IA. Los campos vacíos aparecerán como
              <em> «[Pendiente de completar]»</em> en el borrador.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="c-legal-name">Razón social</Label>
            <Input id="c-legal-name" data-testid="ajustes-company-legal-name" value={form.company_legal_name} onChange={set("company_legal_name")} className="mt-1" placeholder="MercaGo S.L." />
          </div>
          <div>
            <Label htmlFor="c-trade-name">Nombre comercial</Label>
            <Input id="c-trade-name" data-testid="ajustes-company-trade-name" value={form.company_trade_name} onChange={set("company_trade_name")} className="mt-1" placeholder="MercaGo" />
          </div>
          <div>
            <Label htmlFor="c-tax-id">NIF / CIF</Label>
            <Input id="c-tax-id" data-testid="ajustes-company-tax-id" value={form.company_tax_id} onChange={set("company_tax_id")} className="mt-1" placeholder="B12345678" />
          </div>
          <div>
            <Label htmlFor="c-phone">Teléfono</Label>
            <Input id="c-phone" data-testid="ajustes-company-phone" value={form.company_phone} onChange={set("company_phone")} className="mt-1" placeholder="+34612345678" />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="c-address">Domicilio fiscal</Label>
            <Input id="c-address" data-testid="ajustes-company-address" value={form.company_address} onChange={set("company_address")} className="mt-1" placeholder="Calle …, 28001 Madrid, España" />
          </div>
          <div>
            <Label htmlFor="c-email">Email de contacto</Label>
            <Input id="c-email" data-testid="ajustes-company-email" type="email" value={form.company_email} onChange={set("company_email")} className="mt-1" placeholder="info@mercago.one" />
          </div>
          <div>
            <Label htmlFor="c-dpo">Email del DPO</Label>
            <Input id="c-dpo" data-testid="ajustes-company-dpo-email" type="email" value={form.company_dpo_email} onChange={set("company_dpo_email")} className="mt-1" placeholder="dpo@mercago.one (opcional)" />
          </div>
        </div>
      </div>

      <div>
        <Button
          onClick={save}
          disabled={saving || !numberOk}
          data-testid="ajustes-save-btn"
          className="rounded-full bg-[#00953B] hover:bg-[#007a30] text-white gap-2"
        >
          <Save className="h-4 w-4" />
          {saving ? "Guardando…" : "Guardar cambios"}
        </Button>
      </div>

      <div className="rounded-2xl border border-neutral-200 bg-emerald-50/60 p-5 text-sm space-y-2">
        <div className="font-bold text-emerald-900">WhatsApp AI</div>
        <p className="text-emerald-900/80">
          WhatsApp AI está listo. Para activar respuestas automáticas reales por Twilio,
          añade <code className="text-xs px-1 bg-white rounded">TWILIO_ACCOUNT_SID</code>,
          <code className="text-xs px-1 bg-white rounded mx-1">TWILIO_AUTH_TOKEN</code>
          y <code className="text-xs px-1 bg-white rounded">TWILIO_WHATSAPP_FROM</code> en
          <code className="text-xs px-1 bg-white rounded mx-1">/app/backend/.env</code>
          y reinicia el backend.
        </p>
      </div>
    </div>
  );
}
